<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineHeader from './PipelineHeader.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import conversationAPI from 'dashboard/api/inbox/conversation';
import Modal from 'dashboard/components/Modal.vue';
// eslint-disable-next-line no-unused-vars
import CustomContextMenu from 'dashboard/components/ui/CustomContextMenu.vue';
// eslint-disable-next-line no-unused-vars
import SendMessageTemplate from './SendMessageTemplate.vue';
import PipelineCardForm from './PipelineCardForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  currentView: {
    type: String,
    default: 'list',
  },
  pipelineCards: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // Props vindas de PipelineView.vue:411-419 (antes órfãs por
  // fragment-root; ver bug report 2026-04-22 #3). Declaradas para
  // absorver os attrs do pai mesmo quando ListTab não consome (a
  // alternativa seria deixá-los virar warning + atributos HTML
  // soltos no DOM root). O linter reclama de "never used" mas o
  // uso é semântico: bloquear inheritance no DOM.
  // eslint-disable-next-line vue/no-unused-properties
  labelsMap: {
    type: Object,
    default: () => ({}),
  },
  // eslint-disable-next-line vue/no-unused-properties
  itemId: {
    type: [String, Number],
    default: null,
  },
  // eslint-disable-next-line vue/no-unused-properties
  forceReload: {
    type: Number,
    default: 0,
  },
});
// Bug 3 (2026-04-22): emits 'items-updated', 'item-updated' e 'back'
// declarados em PipelineView.vue:421-424 mas não estavam aqui — Vue 3
// não conseguia rotear porque template tinha fragment-root e atributos
// extras viraram warning + ignored. handleEdit setava state mas a
// Modal não estava no template, então usuário clicava lápis e nada
// acontecia visualmente. Agora ambos resolvidos: emits declarados +
// Modal de edit renderizada (ver template). Kebab-case mantido pra
// alinhar com o que PipelineView envia (`@item-updated`, `@items-updated`).
/* eslint-disable vue/no-unused-emit-declarations, vue/custom-event-name-casing */
const emit = defineEmits([
  'switch-view',
  'itemClick',
  'forceUpdate',
  'items-updated',
  'item-updated',
  'back',
]);
/* eslint-enable vue/no-unused-emit-declarations, vue/custom-event-name-casing */
const { t } = useI18n();
const store = useStore();
// eslint-disable-next-line no-unused-vars
const localLoading = ref(false);
const deals = ref([]);
const errorMessage = ref(null);
const conversationData = ref({});
const searchQuery = ref('');
const activeFilters = ref(null);

const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canViewPipeline } = usePipelinePermissions(selectedPipeline);

// Ciclo 7 — teto silencioso de 500 cards na lista. O fetch global traz no
// máximo 500/página; quando meta.has_more, o backend manda meta.next_cursor
// (keyset global (position, id)) e a lista oferece "Carregar mais" no rodapé.
// meta.stage_counts traz o TOTAL real por stage (server-side) — usado nos
// badges dos grupos no lugar de contar o flat parcial ("Lead 177" enganava).
const cardsMeta = computed(() => store.getters['pipelineCards/getMeta'] || {});
const isAppendingFromServer = computed(
  () => store.state.pipelineCards.appending
);
const hasMoreFromServer = computed(
  () =>
    // Mode-aware: has_more/next_cursor podem ser resquício de um load global
    // anterior quando o último load foi do board (que só atualiza
    // stage_counts) — sem o check, o rodapé apareceria e o clique seria
    // no-op (o store bloqueia append fora do modo global; achado Codex).
    store.state.pipelineCards.lastFetchedMode === 'global' &&
    Boolean(cardsMeta.value.has_more) &&
    Boolean(cardsMeta.value.next_cursor)
);
const serverStageCounts = computed(() => cardsMeta.value.stage_counts || {});
const serverTotalCount = computed(() =>
  Object.values(serverStageCounts.value).reduce(
    (sum, count) => sum + (Number(count) || 0),
    0
  )
);
// Busca/filtros da lista são client-side sobre o flat carregado; nesses casos
// o badge volta a contar o filtrado local (stage_counts não reflete a busca).
const hasClientSideFiltering = computed(
  () => Boolean(searchQuery.value) || Boolean(activeFilters.value)
);
const stageServerTotal = stage => {
  if (hasClientSideFiltering.value) return null;
  const count = serverStageCounts.value[stage.id];
  return count === undefined ? null : Number(count) || 0;
};
const stageCountLabel = stage => {
  const total = stageServerTotal(stage);
  if (total === null || total <= stage.deals.length) {
    return String(total ?? stage.deals.length);
  }
  return t('PIPELINE.LIST.STAGE_COUNT_PARTIAL', {
    loaded: stage.deals.length,
    total,
  });
};

const handleLoadMoreFromServer = async () => {
  if (isAppendingFromServer.value) return;
  try {
    await store.dispatch('pipelineCards/fetchPipelineCardItems', {
      append: true,
    });
  } catch (error) {
    // Erro já toasted por handlePipelineError no store; o botão permanece
    // (has_more não muda em falha) e o usuário pode tentar de novo.
  }
};

const showDeleteModal = ref(false);
const itemToDelete = ref(null);
const isDeleting = ref(false);

// Adicione um ref para controlar as etapas expandidas
const expandedStages = ref(new Set()); // Começa com todas as etapas fechadas

// UX C1 (2026-04-22): Pagination simples por stage para evitar
// renderizar 1000+ DOM nodes de uma vez quando o pipeline tem
// muitos cards. Default: mostra primeiros 30 por stage; "Carregar
// mais" incrementa em +30. Solução pragmática (sem lib externa
// de virtualização) — resolve 90% dos casos com risco mínimo.
const STAGE_RENDER_INITIAL = 30;
const STAGE_RENDER_INCREMENT = 30;
const stageRenderLimits = ref({});

const getStageRenderLimit = stageId =>
  stageRenderLimits.value[stageId] || STAGE_RENDER_INITIAL;

const getVisibleDeals = stage =>
  stage.deals.slice(0, getStageRenderLimit(stage.id));

const hasMoreDeals = stage =>
  stage.deals.length > getStageRenderLimit(stage.id);

const remainingDealsCount = stage =>
  Math.max(0, stage.deals.length - getStageRenderLimit(stage.id));

const loadMoreDeals = stageId => {
  const current = getStageRenderLimit(stageId);
  stageRenderLimits.value = {
    ...stageRenderLimits.value,
    [stageId]: current + STAGE_RENDER_INCREMENT,
  };
};

// Adicione estas refs para controlar o modal de edição
const showEditModal = ref(false);
const itemToEdit = ref(null);

// Sorting state (B5 - List View Enhancement)
const sortConfig = ref({
  field: 'created_at',
  direction: 'desc',
});

// Available sort fields
const sortFields = [
  { id: 'title', label: 'PIPELINE.SORT_OPTIONS.TITLE', icon: 'list' },
  {
    id: 'value',
    label: 'PIPELINE.SORT_OPTIONS.VALUE',
    icon: 'credit-card-person',
  },
  {
    id: 'priority',
    label: 'PIPELINE.SORT_OPTIONS.PRIORITY',
    icon: 'priority-high',
  },
  {
    id: 'created_at',
    label: 'PIPELINE.SORT_OPTIONS.CREATED_AT',
    icon: 'calendar',
  },
  {
    id: 'next_activity',
    label: 'PIPELINE.SORT_OPTIONS.NEXT_ACTIVITY',
    icon: 'calendar-clock',
  },
  {
    id: 'last_activity',
    label: 'PIPELINE.SORT_OPTIONS.LAST_ACTIVITY',
    icon: 'arrow-clockwise',
  },
  {
    id: 'conversation_activity',
    label: 'PIPELINE.SORT_OPTIONS.CONVERSATION_ACTIVITY',
    icon: 'chat',
  },
];

// Priority order for sorting
const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1, none: 0 };

// Toggle sort for a field
const toggleSort = field => {
  if (sortConfig.value.field === field) {
    // Toggle direction
    sortConfig.value.direction =
      sortConfig.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    // New field, default to desc for value/priority, asc for title
    sortConfig.value.field = field;
    sortConfig.value.direction = [
      'value',
      'priority',
      'created_at',
      'last_activity',
      'conversation_activity',
    ].includes(field)
      ? 'desc'
      : 'asc';
  }
};

// Get sort icon for column header
const getSortIcon = field => {
  if (sortConfig.value.field !== field) return 'arrow-sort';
  return sortConfig.value.direction === 'asc' ? 'chevron-up' : 'chevron-down';
};

// Computed para filtrar os deals baseado na busca e filtros
const filteredDeals = computed(() => {
  let filtered = deals.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      deal =>
        deal.title.toLowerCase().includes(query) ||
        deal.description.toLowerCase().includes(query) ||
        deal.assignee?.name.toLowerCase().includes(query)
    );
  }

  if (activeFilters.value) {
    const filters = activeFilters.value;

    filtered = filtered.filter(deal => {
      let matches = true;

      if (filters.priority?.length) {
        matches = matches && filters.priority.includes(deal.priority);
      }

      if (filters.value?.min || filters.value?.max) {
        const dealValue = parseFloat(deal.value) || 0;
        if (filters.value.min) {
          matches = matches && dealValue >= parseFloat(filters.value.min);
        }
        if (filters.value.max) {
          matches = matches && dealValue <= parseFloat(filters.value.max);
        }
      }

      if (filters.agent_id) {
        matches = matches && deal.assignee?.id === filters.agent_id;
      }

      if (filters.date?.start || filters.date?.end) {
        const dealDate = new Date(deal.createdAt);
        if (filters.date.start) {
          matches = matches && dealDate >= new Date(filters.date.start);
        }
        if (filters.date.end) {
          matches = matches && dealDate <= new Date(filters.date.end);
        }
      }

      // Fase 2.1 — filtro SLA excedido (client-side baseado em item_details.sla)
      if (filters.sla_exceeded) {
        const sla = deal.item_details?.sla;
        const stillInSameStage =
          sla && (sla.stage === undefined || sla.stage === deal.pipeline_stage);
        matches = matches && Boolean(sla?.exceeded_at && stillInSameStage);
      }

      return matches;
    });
  }

  // Apply sorting (B5 - List View Enhancement)
  const { field, direction } = sortConfig.value;
  const sortedFiltered = [...filtered].sort((a, b) => {
    let comparison = 0;

    switch (field) {
      case 'title':
        comparison = (a.title || '').localeCompare(b.title || '');
        break;
      case 'value':
        comparison = (parseFloat(a.value) || 0) - (parseFloat(b.value) || 0);
        break;
      case 'priority':
        comparison =
          (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
        break;
      case 'next_activity':
        comparison =
          (a.next_activity_at
            ? new Date(a.next_activity_at).getTime()
            : 9999999999999) -
          (b.next_activity_at
            ? new Date(b.next_activity_at).getTime()
            : 9999999999999);
        break;
      case 'last_activity':
        comparison =
          (a.last_pipeline_activity_at
            ? new Date(a.last_pipeline_activity_at).getTime()
            : 0) -
          (b.last_pipeline_activity_at
            ? new Date(b.last_pipeline_activity_at).getTime()
            : 0);
        break;
      case 'conversation_activity':
        comparison =
          (a.conversation_last_activity_at
            ? new Date(a.conversation_last_activity_at).getTime()
            : 0) -
          (b.conversation_last_activity_at
            ? new Date(b.conversation_last_activity_at).getTime()
            : 0);
        break;
      case 'created_at':
      default:
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
        break;
    }

    return direction === 'asc' ? comparison : -comparison;
  });

  return sortedFiltered;
});

// Export to CSV (B5 - List View Enhancement)
const exportToCSV = () => {
  const currentPipeline = selectedPipeline.value;
  if (!currentPipeline || !filteredDeals.value.length) return;

  const headers = [
    t('PIPELINE.FORM.TITLE.LABEL'),
    t('PIPELINE.STAGE.LABEL'),
    t('PIPELINE.VALUE.LABEL'),
    t('PIPELINE.FORM.PRIORITY.LABEL'),
    t('PIPELINE.AGENT.LABEL'),
    t('PIPELINE.FILTER.DATE.LABEL'),
  ];

  const rows = filteredDeals.value.map(deal => [
    `"${(deal.title || '').replace(/"/g, '""')}"`,
    `"${(deal.stageName || '').replace(/"/g, '""')}"`,
    deal.value || 0,
    t(`PIPELINE.PRIORITY_LABELS.${(deal.priority || 'none').toUpperCase()}`),
    `"${(deal.assignee?.name || t('PIPELINE.NO_AGENT')).replace(/"/g, '""')}"`,
    deal.createdAt,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const fileName = `${currentPipeline.name}_${new Date().toISOString().split('T')[0]}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Cache dos funis para facilitar a busca de cores/nomes de etapas
// eslint-disable-next-line no-unused-vars
const pipelinesCache = computed(() => {
  // Obtém todos os funis do store
  const allPipelines = store.getters['pipelines/getPipelines'] || [];

  // Cria um mapa indexado por ID para busca mais rápida
  const cache = {};
  allPipelines.forEach(pipeline => {
    cache[pipeline.id] = pipeline;
  });

  return cache;
});

const processDeals = () => {
  try {
    const currentPipeline = selectedPipeline.value;
    if (!currentPipeline?.id) {
      errorMessage.value = 'Nenhum funil selecionado';
      return;
    }

    // Filtra apenas os itens do funil atual selecionado - convertendo IDs para string para garantir compatibilidade
    const currentPipelineItems = props.pipelineCards.filter(
      item => String(item.pipeline_id) === String(currentPipeline.id)
    );

    deals.value = currentPipelineItems.map(item => {
      // Obtém informações da etapa do funil atual
      const stageName = currentPipeline.stages[item.pipeline_stage]?.name || '';
      const stageColor =
        currentPipeline.stages[item.pipeline_stage]?.color || '#64748B';

      return {
        id: item.id,
        title: item.title || item.item_details?.title || '',
        description: item.description || item.item_details?.description || '',
        priority: item.item_details?.priority || 'none',
        assignee: item.item_details?.agent || null,
        value: item.item_details?.value || 0,
        leadScore: item.item_details?.lead_score ?? null,
        contactName: item.item_details?.contact_name || '',
        contactThumbnail: item.item_details?.contact_thumbnail || '',
        stage: item.pipeline_stage,
        stageName,
        stageColor,
        createdAt: new Date(item.created_at).toLocaleDateString(),
        item_details: item.item_details,
        pipeline_id: String(item.pipeline_id),
      };
    });
  } catch (err) {
    errorMessage.value = err.message;
  }
};

// Watch para processar os items quando recebidos via props
watch(
  () => props.pipelineCards,
  () => processDeals(),
  { immediate: true, deep: true }
);

// Watch para atualizar quando o funil mudar
watch(
  () => selectedPipeline.value,
  () => processDeals()
);

// Watch para atualizar quando os funis no store mudarem
watch(
  () => store.getters['pipelines/getPipelines'],
  () => processDeals()
);

// onMounted para garantir processamento inicial
onMounted(() => {
  if (selectedPipeline.value) {
    processDeals();
  }
});

const handleSearch = query => {
  searchQuery.value = query;
};

const handleFilter = filters => {
  activeFilters.value = filters;
};

const searchResults = computed(() => {
  if (!searchQuery.value) return { total: 0, stages: {} };

  const results = {
    total: filteredDeals.value.length,
    stages: {},
  };

  filteredDeals.value.forEach(deal => {
    if (!results.stages[deal.stageName]) {
      results.stages[deal.stageName] = 0;
    }
    results.stages[deal.stageName] += 1;
  });

  return results;
});

// Novo computed para agrupar os deals por estágio com logging
const groupedDeals = computed(() => {
  const currentPipeline = selectedPipeline.value;
  if (!currentPipeline?.stages) {
    return [];
  }

  // Cria um mapa de grupos inicialmente vazios para todas as etapas do funil atual
  const groups = {};

  Object.entries(currentPipeline.stages).forEach(([stageId, stageInfo]) => {
    groups[stageId] = {
      id: stageId,
      name: stageInfo.name || '',
      color: stageInfo.color || '#64748B',
      deals: [],
      totalValue: 0,
      position: stageInfo.position || 0,
    };
  });

  // Distribui os deals filtrados pelos grupos
  filteredDeals.value.forEach(deal => {
    if (groups[deal.stage]) {
      groups[deal.stage].deals.push(deal);
      groups[deal.stage].totalValue += Number(deal.value) || 0;
    }
  });

  // Converte para array e ordena por posição
  const result = Object.values(groups).sort((a, b) => a.position - b.position);

  return result;
});

// Função auxiliar para formatar o nome da etapa para exibição
// eslint-disable-next-line no-unused-vars
const getFriendlyStageLabel = stageKey => {
  // Transforma "etapa_1" em "Etapa 1", "triagem" em "Triagem", etc.
  if (stageKey.startsWith('etapa_')) {
    const num = stageKey.replace('etapa_', '');
    return `Etapa ${num}`;
  }
  // Capitaliza o primeiro caractere para outros casos
  return stageKey.charAt(0).toUpperCase() + stageKey.slice(1);
};

// Função para carregar dados da conversa
const fetchConversationData = async deal => {
  if (!deal.item_details?.conversation_id) return;

  try {
    const response = await conversationAPI.get({});
    const conversations = response.data.data.payload;
    conversationData.value[deal.id] = conversations.find(
      c => c.id === deal.item_details.conversation_id
    );
  } catch (error) {
    // Error handled silently
  }
};

// Handlers
const handleEdit = deal => {
  itemToEdit.value = {
    id: deal.id,
    title: deal.title,
    description: deal.description,
    pipeline_id: selectedPipeline.value?.id,
    pipeline_stage: deal.stage,
    item_details: {
      title: deal.title,
      description: deal.description,
      priority: deal.priority,
      value: deal.value,
      agent_id: deal.assignee?.id,
    },
    custom_attributes: deal.custom_attributes || {},
  };
  showEditModal.value = true;
};

const handleDelete = deal => {
  itemToDelete.value = deal;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!itemToDelete.value) return;

  try {
    isDeleting.value = true;
    await PipelineCardsAPI.deleteItem(itemToDelete.value.id);
    emit('forceUpdate');
    showDeleteModal.value = false;
    itemToDelete.value = null;
  } catch (error) {
    // Error handled silently
  } finally {
    isDeleting.value = false;
  }
};

const handleItemClick = deal => {
  emit('itemClick', deal);
};

// Bug 3 (2026-04-22): chamado quando PipelineCardForm emite 'saved'.
// Fecha a Modal, limpa state e propaga 'item-updated' pro pai
// (PipelineView.vue:424 → handleItemsUpdated). Também emite
// 'forceUpdate' por compatibilidade com flow antigo de delete.
const handleItemUpdated = updated => {
  showEditModal.value = false;
  itemToEdit.value = null;
  // eslint-disable-next-line vue/custom-event-name-casing
  emit('item-updated', updated);
  emit('forceUpdate');
};

// Simplificar o watch para evitar loops aninhados
watch(groupedDeals, async newGroups => {
  // Processa cada grupo em paralelo
  const fetchPromises = newGroups.flatMap(group =>
    group.deals
      .filter(deal => deal.item_details?.conversation_id)
      .map(fetchConversationData)
  );

  await Promise.all(fetchPromises);
});

// Função para alternar expansão da etapa
const toggleStage = stageId => {
  if (expandedStages.value.has(stageId)) {
    expandedStages.value.delete(stageId);
  } else {
    expandedStages.value.add(stageId);
  }
};

// Função para verificar se uma etapa está expandida
const isStageExpanded = stageId => expandedStages.value.has(stageId);

// Get priority bar color (B5 - List View Enhancement)
const getPriorityBarColor = priority => {
  const colors = {
    urgent: '#ef4444', // red-500
    high: '#f97316', // orange-500
    medium: '#eab308', // yellow-500
    low: '#3b82f6', // blue-500
    none: 'transparent',
  };
  return colors[priority] || colors.none;
};

// Get lead score badge class
const getLeadScoreBadgeClass = score => {
  if (score == null) return '';
  if (score >= 80) return 'bg-n-teal-3 text-n-teal-11';
  if (score >= 60) return 'bg-n-blue-3 text-n-blue-11';
  if (score >= 40) return 'bg-n-amber-3 text-n-amber-11';
  if (score >= 20) return 'bg-n-orange-3 text-n-orange-11';
  return 'bg-n-alpha-2 text-n-slate-11';
};

// Get priority badge class (B5 - List View Enhancement)
const getPriorityBadgeClass = priority => {
  const classes = {
    urgent: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-9',
    high: 'bg-n-orange-3 text-n-orange-11 border-n-orange-9',
    medium: 'bg-n-amber-3 text-n-amber-11 border-n-amber-9',
    low: 'bg-n-blue-3 text-n-blue-11 border-n-blue-9',
    none: 'bg-n-alpha-2 text-n-slate-11 border-n-weak',
  };
  return classes[priority] || classes.none;
};
</script>

<template>
  <!-- Bug 3 (2026-04-22): single root <div> evita fragment-root warning
       que impedia atributos extras (labels-map, item-id, force-reload)
       e listeners (@items-updated, @item-updated, @back) de inheritar
       quando ListTab é renderizado via <component :is> em PipelineView. -->
  <div class="flex flex-col h-full w-full">
    <!-- Access denied: agent is not a member of this pipeline -->
    <div
      v-if="!canViewPipeline"
      class="flex flex-col h-full w-full bg-n-background items-center justify-center p-8"
    >
      <div class="text-center max-w-sm">
        <fluent-icon
          icon="lock-closed"
          size="40"
          class="mx-auto mb-4 text-n-slate-10"
        />
        <h3 class="text-base font-semibold text-n-slate-12 mb-2">
          {{ t('PIPELINE.ACCESS_DENIED.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('PIPELINE.ACCESS_DENIED.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <div class="flex flex-col h-full w-full bg-n-background">
        <PipelineHeader
          :current-view="currentView"
          :search-results="searchResults"
          :active-filters="activeFilters"
          :pipeline-cards="pipelineCards"
          @filter="handleFilter"
          @search="handleSearch"
          @switch-view="view => emit('switch-view', view)"
        />

        <!-- List Toolbar (B5 - List View Enhancement) -->
        <div
          class="flex items-center justify-between px-4 py-2 border-b border-n-weak bg-n-solid-2"
        >
          <div class="flex items-center gap-4">
            <span class="text-sm text-n-slate-11">
              {{ filteredDeals.length }}
              {{
                filteredDeals.length === 1
                  ? t('PIPELINE.CARD_SINGULAR')
                  : t('PIPELINE.CARDS')
              }}
            </span>
            <div class="flex items-center">
              <span class="text-xs text-n-slate-10 mr-2">
                {{ t('PIPELINE.SORT_MODAL.SORT_BY') }}:
              </span>
              <div class="flex items-center gap-1">
                <button
                  v-for="field in sortFields"
                  :key="field.id"
                  class="flex items-center gap-0.5 px-2 py-1 rounded text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                  :class="{
                    'bg-n-alpha-2 text-n-brand': sortConfig.field === field.id,
                  }"
                  :title="t(field.label)"
                  @click="toggleSort(field.id)"
                >
                  <fluent-icon :icon="field.icon" size="14" />
                  <fluent-icon
                    :icon="getSortIcon(field.id)"
                    size="12"
                    :class="
                      sortConfig.field === field.id
                        ? 'opacity-100'
                        : 'opacity-50'
                    "
                  />
                </button>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              :disabled="!filteredDeals.length"
              :title="t('PIPELINE.LIST.EXPORT_CSV')"
              @click="exportToCSV"
            >
              <template #icon>
                <fluent-icon icon="arrow-download" size="16" />
              </template>
              <span class="hidden lg:inline">{{
                t('PIPELINE.LIST.EXPORT_CSV')
              }}</span>
            </Button>
          </div>
        </div>

        <div class="min-h-0 w-full p-4 flex-1 overflow-auto">
          <!-- Loading State -->
          <div v-if="isLoading" class="flex justify-center items-center py-12">
            <span
              class="w-6 h-6 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
            />
          </div>

          <!-- Error State -->
          <div
            v-else-if="errorMessage"
            class="flex justify-center items-center py-12 text-n-ruby-11"
          >
            {{ errorMessage }}
          </div>

          <!-- Empty State -->
          <div
            v-else-if="!filteredDeals.length"
            class="flex flex-col items-center justify-center py-12"
          >
            <fluent-icon
              icon="checkmark-circle"
              size="48"
              class="mb-4 text-n-slate-10"
            />
            <p class="text-lg text-n-slate-11">{{ t('PIPELINE.NO_ITEMS') }}</p>
          </div>

          <!-- Simplified List View -->
          <div v-else class="space-y-2 w-full max-w-full">
            <div
              v-for="stage in groupedDeals"
              :key="stage.id"
              class="deal-group w-full bg-n-solid-2 rounded-xl outline outline-1 outline-n-weak animate-fade-in"
            >
              <!-- Stage Header -->
              <div
                class="stage-header p-3 flex items-center justify-between bg-n-solid-3 rounded-t-xl cursor-pointer"
                @click="toggleStage(stage.id)"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="w-3 h-3 rounded-full"
                    :style="{ backgroundColor: stage.color }"
                  />
                  <h3 class="font-medium text-n-slate-12">
                    {{ stage.name }}
                  </h3>
                  <!-- Ciclo 7 — total real do stage (meta.stage_counts);
                       parcial vira "carregados de total". -->
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ stageCountLabel(stage) }}
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{
                      new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: t('PIPELINE.CURRENCY'),
                      }).format(stage.totalValue)
                    }}
                  </div>
                  <fluent-icon
                    :icon="
                      isStageExpanded(stage.id) ? 'chevron-up' : 'chevron-down'
                    "
                    size="16"
                    class="text-n-slate-11"
                  />
                </div>
              </div>

              <!-- Column Headers -->
              <!-- eslint-disable vue/no-static-inline-styles -->
              <div
                v-show="isStageExpanded(stage.id)"
                class="grid items-center gap-3 px-4 py-1.5 ml-1 border-b border-n-weak bg-n-alpha-1 text-[11px] font-medium text-n-slate-11 uppercase tracking-wider"
                style="
                  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) auto auto auto auto;
                "
              >
                <!-- eslint-enable vue/no-static-inline-styles -->
                <span>{{ t('PIPELINE.FORM.TITLE.LABEL') }}</span>
                <span>{{ t('PIPELINE.AGENT.LABEL') }}</span>
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <span class="w-16 text-center">Score</span>
                <span class="w-20 text-center">{{
                  t('PIPELINE.FORM.PRIORITY.LABEL')
                }}</span>
                <span class="w-24 text-right">{{
                  t('PIPELINE.VALUE.LABEL')
                }}</span>
                <span class="w-16" />
              </div>

              <!-- Deals List — UX C1 (2026-04-22): renderiza apenas
                   getVisibleDeals(stage) (default 30 por stage).
                   Botão "Carregar mais" abaixo incrementa em +30. -->
              <div
                v-show="isStageExpanded(stage.id)"
                class="divide-y divide-n-weak"
              >
                <div
                  v-for="deal in getVisibleDeals(stage)"
                  :key="deal.id"
                  class="group flex items-stretch p-0 bg-n-solid-2 hover:bg-n-alpha-1 cursor-pointer transition-all duration-200 border-b border-n-weak last:border-0"
                  @click="handleItemClick(deal)"
                >
                  <!-- Priority indicator bar -->
                  <div
                    class="w-1 flex-shrink-0 transition-colors"
                    :style="{
                      backgroundColor: getPriorityBarColor(deal.priority),
                    }"
                  />

                  <!-- eslint-disable vue/no-static-inline-styles -->
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                  <div
                    class="flex-1 grid items-center gap-3 px-4 py-3"
                    style="
                      grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) auto auto auto auto;
                    "
                  >
                    <!-- eslint-enable vue/no-static-inline-styles -->
                    <!-- Col 1: Contact avatar + Title/Description -->
                    <div class="flex items-center gap-3 min-w-0">
                      <span
                        v-if="deal.contactThumbnail"
                        class="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden"
                      >
                        <img
                          :src="deal.contactThumbnail"
                          :alt="deal.contactName"
                          class="w-full h-full object-cover"
                        />
                      </span>
                      <span
                        v-else
                        class="flex-shrink-0 w-8 h-8 rounded-full bg-n-alpha-2 flex items-center justify-center text-xs font-medium text-n-slate-11"
                      >
                        {{ (deal.title || '?').charAt(0).toUpperCase() }}
                      </span>
                      <div class="min-w-0">
                        <div
                          class="font-medium text-sm text-n-slate-12 truncate"
                        >
                          {{ deal.title }}
                        </div>
                        <div
                          v-if="deal.description"
                          class="text-xs text-n-slate-11 truncate"
                        >
                          {{ deal.description.substring(0, 60) }}
                        </div>
                      </div>
                    </div>

                    <!-- Col 2: Agent -->
                    <div
                      class="flex items-center gap-2 min-w-0"
                      :title="deal.assignee?.name"
                    >
                      <template v-if="deal.assignee">
                        <img
                          v-if="deal.assignee.avatar"
                          :src="deal.assignee.avatar"
                          :alt="deal.assignee.name"
                          class="flex-shrink-0 w-6 h-6 rounded-full object-cover"
                        />
                        <span
                          v-else
                          class="flex-shrink-0 w-6 h-6 rounded-full bg-n-brand flex items-center justify-center text-[10px] font-medium text-white"
                        >
                          {{ deal.assignee.name.charAt(0) }}
                        </span>
                        <span class="text-xs text-n-slate-11 truncate">
                          {{ deal.assignee.name }}
                        </span>
                      </template>
                      <span v-else class="text-xs text-n-slate-10 italic">
                        {{ t('PIPELINE.NO_AGENT') }}
                      </span>
                    </div>

                    <!-- Col 3: Lead Score -->
                    <div class="flex items-center justify-center w-16">
                      <span
                        v-if="deal.leadScore != null"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full"
                        :class="getLeadScoreBadgeClass(deal.leadScore)"
                      >
                        {{ deal.leadScore }}
                      </span>
                      <span v-else class="text-xs text-n-slate-9">{{
                        t('PIPELINE.LIST.EMPTY_CELL')
                      }}</span>
                    </div>

                    <!-- Col 4: Priority Badge -->
                    <div class="flex items-center justify-center w-20">
                      <span
                        v-if="deal.priority && deal.priority !== 'none'"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border"
                        :class="getPriorityBadgeClass(deal.priority)"
                      >
                        <fluent-icon icon="alert" size="10" />
                        {{
                          t(
                            `PIPELINE.PRIORITY_LABELS.${deal.priority.toUpperCase()}`
                          )
                        }}
                      </span>
                      <span v-else class="text-xs text-n-slate-9">{{
                        t('PIPELINE.LIST.EMPTY_CELL')
                      }}</span>
                    </div>

                    <!-- Col 5: Value + Date stacked -->
                    <div class="text-right w-24">
                      <div
                        class="text-sm font-semibold text-n-slate-12 tabular-nums"
                      >
                        {{
                          new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: t('PIPELINE.CURRENCY'),
                          }).format(deal.value)
                        }}
                      </div>
                      <div class="text-[11px] text-n-slate-10 tabular-nums">
                        {{ deal.createdAt }}
                      </div>
                    </div>

                    <!-- Col 6: Actions -->
                    <div
                      class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all w-16 justify-end"
                    >
                      <button
                        class="p-1.5 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
                        :title="t('PIPELINE.EDIT')"
                        @click.stop="handleEdit(deal)"
                      >
                        <fluent-icon icon="edit" size="14" />
                      </button>
                      <button
                        class="p-1.5 rounded-lg text-n-slate-11 hover:text-n-ruby-11 hover:bg-n-ruby-3 transition-colors"
                        :title="t('PIPELINE.DELETE')"
                        @click.stop="handleDelete(deal)"
                      >
                        <fluent-icon icon="delete" size="14" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- UX C1: "Carregar mais" botão quando stage tem mais
                   deals que o limit atual de render. -->
              <div
                v-if="isStageExpanded(stage.id) && hasMoreDeals(stage)"
                class="px-4 py-3 border-t border-n-weak bg-n-alpha-1 flex items-center justify-center"
              >
                <button
                  class="text-sm font-medium text-n-brand hover:text-n-brand-emphasis hover:bg-n-alpha-2 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  @click="loadMoreDeals(stage.id)"
                >
                  <fluent-icon icon="chevron-down" size="14" />
                  {{
                    t('PIPELINE.LIST.LOAD_MORE', {
                      count: remainingDealsCount(stage),
                    })
                  }}
                </button>
              </div>
            </div>

            <!-- Ciclo 7 — rodapé de paginação global por cursor: remove o
                 teto silencioso de 500 cards da lista. Visível enquanto o
                 backend sinaliza has_more + next_cursor no fetch global. -->
            <div
              v-if="hasMoreFromServer"
              class="flex flex-col items-center gap-2 py-4"
            >
              <span
                v-if="serverTotalCount > deals.length"
                class="text-xs text-n-slate-10"
              >
                {{
                  t('PIPELINE.LIST.SHOWING_OF', {
                    loaded: deals.length,
                    total: serverTotalCount,
                  })
                }}
              </span>
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :is-loading="isAppendingFromServer"
                :disabled="isAppendingFromServer"
                @click="handleLoadMoreFromServer"
              >
                <template #icon>
                  <fluent-icon icon="chevron-down" size="16" />
                </template>
                {{ t('PIPELINE.LOAD_MORE_SIMPLE') }}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Simplified modals -->
      <Modal
        v-model:show="showDeleteModal"
        @close="
          () => {
            showDeleteModal = false;
            itemToDelete = null;
          }
        "
      >
        <div class="p-6">
          <h3 class="text-lg font-medium mb-4">
            {{ t('PIPELINE.DELETE_CONFIRMATION.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-6">
            {{
              t('PIPELINE.DELETE_CONFIRMATION.DESCRIPTION', {
                name: itemToDelete?.title,
              })
            }}
          </p>
          <div class="flex justify-end gap-2">
            <button
              variant="clear"
              size="small"
              @click="showDeleteModal = false"
            >
              {{ t('PIPELINE.CANCEL') }}
            </button>
            <button
              variant="danger"
              size="small"
              :loading="isDeleting"
              @click="confirmDelete"
            >
              {{ t('PIPELINE.DELETE') }}
            </button>
          </div>
        </div>
      </Modal>

      <!-- Bug 3 (2026-04-22): Modal de edição do card na view Lista.
         Antes handleEdit setava showEditModal=true mas não havia modal
         renderizada, fazendo o lápis não responder. Agora usa o mesmo
         padrão de AgendaTab/PipelineCardDetailView (PipelineCardForm
         com is-editing + :initial-data). -->
      <Modal
        v-model:show="showEditModal"
        @close="
          () => {
            showEditModal = false;
            itemToEdit = null;
          }
        "
      >
        <div class="w-[560px]">
          <PipelineCardForm
            v-if="itemToEdit && selectedPipeline"
            :pipeline-id="selectedPipeline.id"
            :stage="itemToEdit.pipeline_stage"
            :initial-data="itemToEdit"
            is-editing
            @saved="handleItemUpdated"
            @close="
              () => {
                showEditModal = false;
                itemToEdit = null;
              }
            "
          />
        </div>
      </Modal>
    </template>
  </div>
</template>

<style scoped>
/* Deal group fade-in animation */
.deal-group {
  animation: fadeIn 0.2s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.deal-group:nth-child(1) {
  animation-delay: 0s;
}
.deal-group:nth-child(2) {
  animation-delay: 0.05s;
}
.deal-group:nth-child(3) {
  animation-delay: 0.1s;
}
.deal-group:nth-child(4) {
  animation-delay: 0.15s;
}
.deal-group:nth-child(5) {
  animation-delay: 0.2s;
}
</style>
